import { api, ApiError } from "../api.ts";
import { artUrl, gameById, getCatalogue, PREPLAY_LABEL, type Game } from "../catalogue.ts";
import { actionButton, chips, coverStyle, playButton } from "../components.ts";
import { clear, errorBox, fmtDate, fmtPlaytime, h, link, section, toast } from "../dom.ts";
import { downloadPath } from "../play.ts";
import { setTitle } from "../router.ts";
import { currentMe, signedIn } from "../session.ts";
import { selling } from "../selling.ts";
import { gameClips } from "./clips.ts";

export interface Review {
  id: string;
  user_id: string;
  recommended: boolean;
  body: string;
  version: string;
  playtime_seconds: number;
  previous_versions: string[];
  burst: boolean;
  created_at: number;
  updated_at: number;
}

export interface ReviewSummary {
  total: number;
  counted: number;
  positive: number;
  percent_positive: number | null;
  excluded_burst: number;
  burst_active: boolean;
  min_playtime_seconds: number;
}

interface PublicUser {
  id: string;
  handle: string | null;
  displayName: string | null;
}

const names = new Map<string, Promise<string>>();
const label = (u: PublicUser | undefined) => u?.displayName ?? (u?.handle ? `@${u.handle}` : "Player");
let batch: Map<string, (name: string) => void> | null = null;

/**
 * Display name for a user id, as this viewer may see it. Signed in, ids are batched into one
 * `POST /users/lookup` (accounts, wave 2); otherwise, or on an older accounts, one public read per user.
 */
export function userName(id: string): Promise<string> {
  let p = names.get(id);
  if (p) return p;
  if (!signedIn()) {
    p = api<PublicUser>("accounts", `/users/${encodeURIComponent(id)}`, { auth: false }).then(label, () => "Player");
  } else {
    p = new Promise<string>((resolve) => {
      if (!batch) {
        const mine = (batch = new Map());
        queueMicrotask(async () => {
          batch = null;
          const ids = [...mine.keys()];
          try {
            const r = await api<{ users: PublicUser[] }>("accounts", "/users/lookup", { body: { ids: ids.slice(0, 100) } });
            for (const id of ids) mine.get(id)!(label(r.users.find((u) => u.id === id)));
          } catch {
            for (const id of ids) api<PublicUser>("accounts", `/users/${encodeURIComponent(id)}`).then(label, () => "Player").then(mine.get(id)!);
          }
        });
      }
      batch.set(id, resolve);
    });
  }
  names.set(id, p);
  return p;
}

export function nameSpan(id: string): HTMLElement {
  const el = h("span", { class: "who" }, "Player");
  void userName(id).then((n) => (el.textContent = n));
  return el;
}

export function summaryLine(s: ReviewSummary): string {
  if (s.counted === 0) return s.total ? "No counted reviews yet" : "No reviews yet";
  return `${s.percent_positive ?? 0}% recommend · ${s.counted} ${s.counted === 1 ? "review" : "reviews"}`;
}

export async function gamePage(params: Record<string, string>): Promise<HTMLElement> {
  const cat = await getCatalogue();
  const g = gameById(cat, params.id ?? "");
  if (!g) throw new Error("That game isn't in the catalogue.");
  setTitle(g.title);
  const title = artUrl(g.art, "title");

  return h(
    "article",
    { class: "game" },
    h(
      "header",
      { class: "game-hero", style: coverStyle(g, "full") },
      h(
        "div",
        { class: "game-hero-text" },
        title ? h("img", { class: "slide-title-art", src: title, alt: "" }) : null,
        h("h1", { class: title ? "sr-only" : "" }, g.title),
        h("p", { class: "slide-by" }, "by ", link(`/c/${g.creator.id}`, null, g.creator.name)),
        h("div", { class: "slide-actions" }, playButton(g), link(downloadPath(g.id), { class: "btn ghost" }, "Get Banger")),
      ),
    ),
    h(
      "div",
      { class: "game-body" },
      h(
        "div",
        { class: "game-main" },
        h("p", { class: "lead" }, g.blurb),
        g.note ? h("p", { class: "dim" }, g.note) : null,
        chips(g),
        g.facets.prePlay.length
          ? h("p", { class: "dim small" }, "Before you play: ", g.facets.prePlay.map((p) => PREPLAY_LABEL[p] ?? p).join(", "), ".")
          : null,
        gameClips(g.id, g.title, null, "Clips"),
        reviewsSection(g),
      ),
      h("aside", { class: "game-side" }, communitySide(g), aboutSide(g)),
    ),
  );
}

function aboutSide(g: Game): HTMLElement {
  return h(
    "section",
    { class: "side-box" },
    h("h2", null, "Creator"),
    h("p", null, link(`/c/${g.creator.id}`, null, g.creator.name)),
    g.creator.claimedBy ? h("p", { class: "dim small" }, "Verified creator on Banger") : h("p", { class: "dim small" }, "Not claimed yet. ", link("/dashboard", null, "Is this yours?")),
    tipBox(g, currentMe()?.id ?? null),
  );
}

/** Who sees "Tip the creator": only for a claimed creator (money pays the claimant), and never on your own game. */
export function canTip(g: Pick<Game, "creator">, viewerId: string | null): boolean {
  return !!g.creator.claimedBy && g.creator.claimedBy !== viewerId;
}

/** Tipping happens in Threedom (the website is browse only): the button opens the game there with the tip action. */
function tipBox(g: Game, viewerId: string | null): HTMLElement | null {
  if (!selling() || !canTip(g, viewerId)) return null; // no tips while coins can't be sold (wave 9)
  return h(
    "div",
    { class: "tip-box" },
    actionButton(g, "tip", "Tip the creator", "btn small"),
    // tips take the usual 20%, like every coin spend (docs/PLAN.md, owner 2026-10-16)
    h("p", { class: "dim small" }, `${g.creator.name} gets 80% of a tip; Banger keeps 20%, as on every coin spend. Tips can't be refunded.`),
  );
}

function communitySide(g: Game): HTMLElement {
  return section(
    "Community",
    async (body) => {
      const c = await api<{ counts: { threads: number; devlogs: number; open_bugs: number; clips: number }; latest_devlog: { id: string; title: string; created_at: number } | null }>(
        "community",
        `/games/${g.id}/community`,
        { auth: false },
      );
      clear(
        body,
        h(
          "dl",
          { class: "stats" },
          h("div", null, h("dt", null, "Discussions"), h("dd", null, c.counts.threads)),
          h("div", null, h("dt", null, "Devlogs"), h("dd", null, c.counts.devlogs)),
          h("div", null, h("dt", null, "Open bugs"), h("dd", null, c.counts.open_bugs)),
        ),
        c.latest_devlog ? h("p", { class: "small" }, "Latest devlog: ", h("strong", null, c.latest_devlog.title), ` (${fmtDate(c.latest_devlog.created_at)})`) : null,
        h("p", { class: "dim small" }, "Bug reports and discussion happen in the Banger app, where the game runs."),
      );
    },
    { class: "side-box" },
  );
}

function reviewsSection(g: Game): HTMLElement {
  return section("Reviews", async (body) => {
    const r = await api<{ summary: ReviewSummary; reviews: Review[] }>("community", `/games/${g.id}/reviews?limit=20`, { auth: false });
    const s = r.summary;
    clear(
      body,
      h(
        "div",
        { class: "review-summary" },
        h("strong", null, summaryLine(s)),
        h("span", { class: "dim small" }, ` Only players with ${fmtPlaytime(s.min_playtime_seconds)}+ of play can review.`),
        s.burst_active ? h("p", { class: "note" }, "A wave of reviews arrived at once; they stay visible but don't count until a moderator has looked.") : null,
      ),
      writeReview(g),
      r.reviews.length
        ? h(
            "ul",
            { class: "reviews" },
            r.reviews.map((rv) =>
              h(
                "li",
                { class: "review" },
                h(
                  "div",
                  { class: "review-head" },
                  h("span", { class: `verdict ${rv.recommended ? "yes" : "no"}` }, rv.recommended ? "Recommended" : "Not recommended"),
                  nameSpan(rv.user_id),
                  h("span", { class: "dim small" }, `${fmtPlaytime(rv.playtime_seconds)} played · v${rv.version} · ${fmtDate(rv.updated_at)}`),
                ),
                rv.body ? h("p", null, rv.body) : null,
                rv.previous_versions.length ? h("p", { class: "dim small" }, `Updated after v${rv.previous_versions.join(", v")}`) : null,
                rv.burst ? h("p", { class: "dim small" }, "Not counted in the score yet (review wave).") : null,
              ),
            ),
          )
        : h("p", { class: "dim" }, "Nobody has reviewed this yet."),
    );
  });
}

function writeReview(g: Game): HTMLElement {
  if (!signedIn()) return h("p", { class: "small" }, link(`/signin?next=/g/${g.id}`, null, "Sign in"), " to review once you've played.");
  const msg = h("div");
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const fd = new FormData(form);
        try {
          await api("community", `/games/${g.id}/reviews/mine`, {
            method: "PUT",
            body: { recommended: fd.get("rec") === "yes", body: String(fd.get("body") ?? ""), version: String(fd.get("version") || "1.0") },
          });
          toast("Review saved");
          form.closest(".section")?.replaceWith(reviewsSection(g));
        } catch (err) {
          const text =
            err instanceof ApiError && err.code === "not_enough_playtime"
              ? "Play a bit longer in the Banger app first: reviews are from players only."
              : err instanceof ApiError && err.code === "own_game"
                ? "Creators can't review their own game."
                : null;
          clear(msg, text ? h("p", { class: "error", role: "alert" }, text) : errorBox(err));
        }
      },
    },
    h(
      "fieldset",
      { class: "row" },
      h("legend", null, "Your review"),
      h("label", { class: "check" }, h("input", { type: "radio", name: "rec", value: "yes", checked: true }), "Recommend"),
      h("label", { class: "check" }, h("input", { type: "radio", name: "rec", value: "no" }), "Don't recommend"),
    ),
    h("label", { for: "rv-body", class: "sr-only" }, "Review text"),
    h("textarea", { id: "rv-body", name: "body", rows: 3, maxlength: 8000, placeholder: "What should other players know?" }),
    h("label", { class: "inline small" }, "Version played ", h("input", { name: "version", value: "1.0", size: 6, maxlength: 20 })),
    h("button", { class: "btn", type: "submit" }, "Post review"),
    msg,
  );
  return h("details", { class: "write-review" }, h("summary", null, "Write a review"), form);
}
